import { createSelector, Selector } from '@reduxjs/toolkit';
import {
  tLeaderboardTable,
  ILessonTaskBase,
  ITaskSession
} from 'common/modules/ReactShared';
import { selectShared } from '../sharedSelectors';
import { tSharedAppState, tSharedStore } from '../types';

const selectLessonTaskStore: Selector<tSharedAppState, tSharedStore['lessonTask']> =
  createSelector([selectShared], (state) => state.lessonTask);

const selectLessonTask: Selector<tSharedAppState, tSharedStore['lessonTask']['data']> =
  createSelector([selectLessonTaskStore], (lessonTaskStore) => lessonTaskStore.data);

const selectLessonTaskItems: Selector<
  tSharedAppState,
  ILessonTaskBase['items'] | undefined
> = createSelector([selectLessonTask], (lessonTask) => lessonTask?.items);

const selectLessonTaskSession: Selector<
  tSharedAppState,
  tSharedStore['lessonTask']['session'] | undefined
> = createSelector(
  [selectLessonTaskStore],
  (lessonTaskStore) => lessonTaskStore?.session
);

const selectLessonTaskSessionLeaderboard: Selector<
  tSharedAppState,
  tLeaderboardTable | undefined
> = createSelector(
  [selectLessonTaskSession],
  (lessonTaskSession) => lessonTaskSession?.leaderBoard
);

function sortItemSessions(
  itemSessions: ITaskSession['taskItemSessions'],
  lessonTaskItems: ILessonTaskBase['items']
): ITaskSession['taskItemSessions'] {
  const newTaskItemSessions: typeof itemSessions = [];

  lessonTaskItems.forEach((item) => {
    const itemSession = itemSessions.find((session) => session.taskItemId === item.id);
    if (itemSession) {
      newTaskItemSessions.push(itemSession);
    }
  });

  return newTaskItemSessions;
}

const selectSortedLessonTaskSession: Selector<tSharedAppState, ITaskSession | null> =
  createSelector(
    [selectLessonTaskItems, selectLessonTaskSession],
    (lessonTaskItems, lessonTaskSession) => {
      if (
        !lessonTaskSession ||
        !lessonTaskItems ||
        lessonTaskSession.taskItemSessions.length !== lessonTaskItems.length
      ) {
        return lessonTaskSession as ITaskSession | null;
      }

      const newTaskItemSessions = sortItemSessions(
        lessonTaskSession.taskItemSessions,
        lessonTaskItems
      );

      newTaskItemSessions.sort();

      return {
        ...lessonTaskSession,
        taskItemSessions: newTaskItemSessions
      };
    }
  );

const lessonTaskSelectors = {
  selectLessonTaskStore,
  selectLessonTask,
  selectLessonTaskSession,
  selectSortedLessonTaskSession,
  selectLessonTaskSessionLeaderboard
};

export default lessonTaskSelectors;
